import type { IconsNames } from '@eqi-investimentos/icons/web';
import type { FC } from 'react';
import { useRouter } from 'next/navigation';
import { Flex, Switch, Text, type FlexProps } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { Icon } from '@/components/elements/Icon';
import { IfRender } from '@/utils/jsx';

export const ProfileItem: FC<ProfileItemProps> = ({
  ID = '',
  leftIcon,
  title,
  type,
  disabled = false,
  isExternal,
  path,
  showArrow = true,
  onClick,
  onChange,
  value,
  showLeftIcon = false,
  ...props
}) => {
  const t = useT();
  const { push } = useRouter();

  const color = colorStyle[String(disabled) as keyof typeof colorStyle];

  const handleClick = () => {
    if (type === 'switch' && !!onChange) return onChange();
    if (type === 'action') return onClick();
    if (isExternal) return window.open(path, '_blank');
    return push(path);
  };

  return (
    <Flex
      id={ID}
      onClickCapture={handleClick}
      {...defaultStyle(disabled)}
      {...props}
    >
      <Flex gap="xs">
        <IfRender condition={!!showLeftIcon}>
          <Icon iconName={leftIcon} color={color} w={24} h={24} />
        </IfRender>
        <Text size="xl" color="content.primary">
          {title}
        </Text>
      </Flex>
      <IfRender condition={type === 'switch'}>
        <Switch isDisabled={disabled} isChecked={value} zIndex={-1} />
      </IfRender>
      <IfRender condition={type !== 'switch'}>
        <IfRender condition={showArrow && !disabled}>
          <Icon iconName="ChevronRight" color={color} w={24} h={24} />
        </IfRender>
        <IfRender condition={disabled}>
          <Text fontSize="xs" lineHeight="0.875rem" color="content.secondary">
            {t('attributes.soon')}
          </Text>
        </IfRender>
      </IfRender>
    </Flex>
  );
};

const defaultStyle = (disabled: boolean): FlexProps => ({
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 5,
  w: 'full',
  borderBottom: '1px solid',
  borderColor: 'border.secondary',
  cursor: 'pointer',
  pointerEvents: disabled ? 'none' : 'auto',
  _hover: {
    '& > div p, svg': {
      color: disabled ? 'content.secondary' : 'content.brand',
    },
  },
  sx: {
    _last: {
      borderBottomWidth: 0,
    },
  },
});

const colorStyle = {
  true: 'content.secondary',
  false: 'content.primary',
};

export type ProfileItemProps = Omit<
  FlexProps,
  'children' | 'onClick' | 'onChange' | 'title'
> & {
  ID?: string;
  leftIcon: IconsNames;
  showLeftIcon?: boolean;
  title: string;
  disabled?: boolean;
  showArrow?: boolean;
} & (
    | {
        type: 'switch';
        onChange: () => void;
        value: boolean;
        isExternal?: never;
        path?: never;
        onClick?: never;
      }
    | {
        type: 'navigation';
        isExternal: boolean;
        path: string;
        onClick?: never;
        value?: never;
        onChange?: never;
      }
    | {
        type: 'action';
        onClick: () => void;
        isExternal?: never;
        path?: never;
        onChange?: never;
        value?: never;
      }
  );
